<template>
  <div class="order-statistics">
    <Spin v-if="loading" fix></Spin>

    <!-- 统计概览卡片 -->
    <Row :gutter="16" class="stats-overview">
      <Col span="6" v-for="item in overviewCards" :key="item.key">
        <Card class="stat-card">
          <div class="stat-icon" :style="{ backgroundColor: item.color }">
            <Icon :type="item.icon" size="24" color="#fff" />
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ formatNumber(item.value) }}</div>
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-trend" v-if="item.growth !== undefined">
              <span :class="item.growth >= 0 ? 'up' : 'down'">
                <Icon :type="item.growth >= 0 ? 'ios-arrow-up' : 'ios-arrow-down'" />
                {{ Math.abs(item.growth) }}%
              </span>
              <span class="trend-desc">较上期</span>
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="16">
        <Card title="订单趋势">
          <div ref="orderTrendChart" style="height: 350px;"></div>
        </Card>
      </Col>
      <Col span="8">
        <Card title="订单状态分布">
          <div ref="orderStatusChart" style="height: 350px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="12">
        <Card title="区域订单排行">
          <div ref="areaOrderChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="订单时段分布">
          <div ref="hourlyChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>

    <!-- 订单数据表格 -->
    <Card title="订单详细统计" style="margin-top: 16px;">
      <Table :columns="tableColumns" :data="tableData" border stripe />
    </Card>
  </div>
</template>

<script>
import { getOrderStatistics } from '@/api/statistics'
import Echarts from 'echarts'

export default {
  name: 'OrderStatistics',
  props: {
    dateRange: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      statsData: null,
      orderTrendChart: null,
      orderStatusChart: null,
      areaOrderChart: null,
      hourlyChart: null,
      tableColumns: [
        { title: '日期', key: 'date', width: 120 },
        { title: '订单数', key: 'orderCount', width: 100 },
        { title: '完成订单', key: 'completedCount', width: 100 },
        { title: '取消订单', key: 'cancelledCount', width: 100 },
        { title: '完成率', key: 'completionRate', width: 100, render: (h, p) => h('span', `${(p.row.completionRate * 100).toFixed(1)}%`) },
        { title: '订单金额', key: 'totalAmount', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${p.row.totalAmount.toFixed(2)}`) },
        { title: '平均订单金额', key: 'avgAmount', width: 120, render: (h, p) => h('span', `¥${p.row.avgAmount.toFixed(2)}`) }
      ],
      tableData: []
    }
  },
  computed: {
    overviewCards() {
      if (!this.statsData) return []
      const data = this.statsData
      return [
        {
          key: 'total',
          label: '总订单数',
          value: data.totalOrders || 0,
          growth: data.orderGrowth,
          icon: 'ios-document',
          color: '#409EFF'
        },
        {
          key: 'completed',
          label: '已完成订单',
          value: data.completedOrders || 0,
          growth: data.completedGrowth,
          icon: 'ios-checkmark-circle',
          color: '#67C23A'
        },
        {
          key: 'avgAmount',
          label: '平均订单金额',
          value: `¥${(data.avgOrderAmount || 0).toFixed(2)}`,
          growth: data.avgAmountGrowth,
          icon: 'logo-yen',
          color: '#E6A23C'
        },
        {
          key: 'completionRate',
          label: '订单完成率',
          value: `${((data.completionRate || 0) * 100).toFixed(1)}%`,
          growth: data.completionRateGrowth,
          icon: 'ios-pie',
          color: '#909399'
        }
      ]
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.loadData()
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const res = await getOrderStatistics(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.statsData = res.data.data
          this.tableData = this.statsData.dailyStats || []
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || '获取订单统计数据失败')
        }
      } catch (error) {
        this.$Message.error('获取订单统计数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      this.initOrderTrendChart()
      this.initOrderStatusChart()
      this.initAreaOrderChart()
      this.initHourlyChart()
    },
    initOrderTrendChart() {
      if (!this.$refs.orderTrendChart || !this.statsData) return
      
      if (this.orderTrendChart) {
        this.orderTrendChart.dispose()
      }
      
      this.orderTrendChart = Echarts.init(this.$refs.orderTrendChart)
      const trendData = this.statsData.orderTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['订单数', '完成订单', '取消订单']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trendData.map(item => item.date)
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '订单数',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.total),
            itemStyle: { color: '#409EFF' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(64,158,255,0.3)' },
                  { offset: 1, color: 'rgba(64,158,255,0.05)' }
                ]
              }
            }
          },
          {
            name: '完成订单',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.completed),
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '取消订单',
            type: 'line',
            smooth: true,
            data: trendData.map(item => item.cancelled),
            itemStyle: { color: '#F56C6C' }
          }
        ]
      }
      
      this.orderTrendChart.setOption(option)
    },
    initOrderStatusChart() {
      if (!this.$refs.orderStatusChart || !this.statsData) return
      
      if (this.orderStatusChart) {
        this.orderStatusChart.dispose()
      }
      
      this.orderStatusChart = Echarts.init(this.$refs.orderStatusChart)
      const statusData = this.statsData.statusDistribution || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 20,
          data: statusData.map(item => item.name)
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: statusData
          }
        ]
      }
      
      this.orderStatusChart.setOption(option)
    },
    initAreaOrderChart() {
      if (!this.$refs.areaOrderChart || !this.statsData) return
      
      if (this.areaOrderChart) {
        this.areaOrderChart.dispose()
      }
      
      this.areaOrderChart = Echarts.init(this.$refs.areaOrderChart)
      const areaData = this.statsData.areaStats || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: areaData.map(item => item.areaName),
          inverse: true
        },
        series: [
          {
            type: 'bar',
            data: areaData.map(item => item.orderCount),
            itemStyle: {
              color: new Echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#83bff6' },
                { offset: 0.5, color: '#188df0' },
                { offset: 1, color: '#188df0' }
              ])
            }
          }
        ]
      }
      
      this.areaOrderChart.setOption(option)
    },
    initHourlyChart() {
      if (!this.$refs.hourlyChart || !this.statsData) return
      
      if (this.hourlyChart) {
        this.hourlyChart.dispose()
      }
      
      this.hourlyChart = Echarts.init(this.$refs.hourlyChart)
      const hourlyData = this.statsData.hourlyDistribution || []
      
      const option = {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: hourlyData.map(item => `${item.hour}:00`),
          axisLabel: {
            interval: 2
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            type: 'bar',
            data: hourlyData.map(item => item.count),
            itemStyle: {
              color: new Echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#E6A23C' },
                { offset: 1, color: '#F5DEB3' }
              ])
            }
          }
        ]
      }
      
      this.hourlyChart.setOption(option)
    },
    formatNumber(value) {
      if (typeof value === 'string') return value
      if (value >= 10000) {
        return (value / 10000).toFixed(1) + '万'
      }
      return value
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', () => {
      this.orderTrendChart?.resize()
      this.orderStatusChart?.resize()
      this.areaOrderChart?.resize()
      this.hourlyChart?.resize()
    })
  },
  beforeDestroy() {
    this.orderTrendChart?.dispose()
    this.orderStatusChart?.dispose()
    this.areaOrderChart?.dispose()
    this.hourlyChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.order-statistics {
  .stats-overview {
    .stat-card {
      display: flex;
      align-items: center;
      padding: 20px;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 28px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 14px;
          color: #909399;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .up {
            color: #67C23A;
          }
          
          .down {
            color: #F56C6C;
          }
          
          .trend-desc {
            color: #909399;
            margin-left: 4px;
          }
        }
      }
    }
  }
}
</style>

